/**
 * Server functions for fixture-type article CRUD.
 *
 * Articles linked to non-shelf fixture types (battery, pallet, bulk, etc.)
 * are resolved by counting fixture occurrences on the project canvas. See
 * `bomComputation.ts` for how these feed into the LoM.
 */
import { createServerFn } from "@tanstack/react-start"
import { getRequest } from "@tanstack/react-start/server"
import { and, asc, eq } from "drizzle-orm"
import { z } from "zod"
import type { SchemaShelveType } from "@/openapi-schema"
import { fixtureTypeArticles } from "@/db/bom-schema"
import { db } from "@/db/db"
import { logAuditEvent } from "@/features/audit-logs/server/auditLogger"
import { assertPermission } from "@/lib/assert-permission.server"
import { authFunctionMiddleware } from "@/lib/auth-middleware"
import {
  ANY_STORE,
  describeStoreConditions,
  sameStoreConditions,
  type StoreConditions,
  StoreConditionsSchema,
} from "../core/storeConditions"
import { type DbOrTransaction, lockConfigVersion } from "./materialRulesDb.server"
import { assertStoreConditionsValid } from "./storeConditionsDb.server"

// Mirror SchemaShelveType. Kept as a string for forward compatibility — extra
// validation happens in the registry-driven UI.
const ShelveTypeSchema = z.string().min(1)

// ─────────────────────────────────────────────────────────────────────────────
// List
// ─────────────────────────────────────────────────────────────────────────────

export const listFixtureTypeArticlesFn = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .inputValidator(z.object({ configVersionId: z.string() }))
  .handler(async ({ data }) => {
    return db.query.fixtureTypeArticles.findMany({
      where: eq(fixtureTypeArticles.configVersionId, data.configVersionId),
      orderBy: [asc(fixtureTypeArticles.shelveType), asc(fixtureTypeArticles.sortOrder)],
    })
  })

// ─────────────────────────────────────────────────────────────────────────────
// Create
// ─────────────────────────────────────────────────────────────────────────────

const CreateFixtureTypeArticleSchema = z.object({
  configVersionId: z.string(),
  shelveType: ShelveTypeSchema,
  articleNumber: z.string().min(1),
  description: z.string().min(1),
  quantity: z.number().int().min(0).default(1),
  sortOrder: z.number().int().default(0),
  conditions: StoreConditionsSchema.default(ANY_STORE),
})

/** The same article for the same fixture and stores only once; other stores can differ (BLU-768). */
async function assertNotTwice(
  tx: DbOrTransaction,
  configVersionId: string,
  shelveType: string,
  articleNumber: string,
  conditions: StoreConditions,
  ownId: string | null,
) {
  const rows = await tx.query.fixtureTypeArticles.findMany({
    where: and(
      eq(fixtureTypeArticles.configVersionId, configVersionId),
      eq(fixtureTypeArticles.shelveType, shelveType as SchemaShelveType),
      eq(fixtureTypeArticles.articleNumber, articleNumber),
    ),
  })
  if (rows.some((r) => r.id !== ownId && sameStoreConditions(r, conditions))) {
    throw new Response(
      `${articleNumber} is already an article of this fixture for ${describeStoreConditions(conditions)}; change that one, or pick other stores`,
      { status: 400 },
    )
  }
}

export const createFixtureTypeArticleFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator(CreateFixtureTypeArticleSchema)
  .handler(async ({ data }) => {
    const request = getRequest()
    await assertPermission(request, "configVersion", "update")

    await assertStoreConditionsValid(data.conditions)
    // The version lock serialises the check and the insert, so two saves
    // can't both add the same article for the same stores.
    const created = await db.transaction(async (tx) => {
      await lockConfigVersion(tx, data.configVersionId)
      await assertNotTwice(
        tx,
        data.configVersionId,
        data.shelveType,
        data.articleNumber,
        data.conditions,
        null,
      )
      const [row] = await tx
        .insert(fixtureTypeArticles)
        .values({
          configVersionId: data.configVersionId,
          shelveType: data.shelveType as SchemaShelveType,
          articleNumber: data.articleNumber,
          description: data.description,
          quantity: data.quantity,
          sortOrder: data.sortOrder,
          ...data.conditions,
        })
        .returning()
      return row
    })

    await logAuditEvent(
      {
        action: "fixtureTypeArticle.create",
        entityType: "fixtureTypeArticle",
        entityId: created.id,
        entityName: `${data.shelveType}/${data.articleNumber}`,
        summary: `Linked article "${data.articleNumber}" to fixture type "${data.shelveType}" for ${describeStoreConditions(data.conditions)}`,
        metadata: { configVersionId: data.configVersionId },
      },
      request,
    )

    return created
  })

// ─────────────────────────────────────────────────────────────────────────────
// Update
// ─────────────────────────────────────────────────────────────────────────────

const UpdateFixtureTypeArticleSchema = z.object({
  id: z.string(),
  shelveType: ShelveTypeSchema.optional(),
  articleNumber: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
  quantity: z.number().int().min(0).optional(),
  sortOrder: z.number().int().optional(),
  conditions: StoreConditionsSchema.optional(),
})

export const updateFixtureTypeArticleFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator(UpdateFixtureTypeArticleSchema)
  .handler(async ({ data }) => {
    const request = getRequest()
    await assertPermission(request, "configVersion", "update")

    const { id, shelveType, conditions, ...rest } = data
    const found = await db.query.fixtureTypeArticles.findFirst({
      where: eq(fixtureTypeArticles.id, id),
      columns: { configVersionId: true },
    })
    if (!found) throw new Response("Fixture type article not found", { status: 404 })
    if (conditions) await assertStoreConditionsValid(conditions)
    const updated = await db.transaction(async (tx) => {
      await lockConfigVersion(tx, found.configVersionId)
      // Read the row again under the lock: a save in between may have changed it.
      const existing = await tx.query.fixtureTypeArticles.findFirst({
        where: eq(fixtureTypeArticles.id, id),
      })
      if (!existing) throw new Response("Fixture type article not found", { status: 404 })
      const next = conditions ?? existing
      await assertNotTwice(
        tx,
        existing.configVersionId,
        shelveType ?? existing.shelveType,
        rest.articleNumber ?? existing.articleNumber,
        next,
        id,
      )
      const [row] = await tx
        .update(fixtureTypeArticles)
        .set({
          ...rest,
          ...(conditions ?? {}),
          ...(shelveType !== undefined ? { shelveType: shelveType as SchemaShelveType } : {}),
          updatedAt: new Date(),
        })
        .where(eq(fixtureTypeArticles.id, id))
        .returning()
      return row
    })

    if (!updated) throw new Response("Fixture type article not found", { status: 404 })

    await logAuditEvent(
      {
        action: "fixtureTypeArticle.update",
        entityType: "fixtureTypeArticle",
        entityId: id,
        entityName: `${updated.shelveType}/${updated.articleNumber}`,
        summary: `Updated fixture-type article "${updated.articleNumber}" (${updated.shelveType}) for ${describeStoreConditions(updated)}`,
      },
      request,
    )

    return updated
  })

// ─────────────────────────────────────────────────────────────────────────────
// Delete
// ─────────────────────────────────────────────────────────────────────────────

export const deleteFixtureTypeArticleFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator(z.object({ id: z.string() }))
  .handler(async ({ data }) => {
    const request = getRequest()
    await assertPermission(request, "configVersion", "update")

    const existing = await db.query.fixtureTypeArticles.findFirst({
      where: eq(fixtureTypeArticles.id, data.id),
    })
    if (!existing) throw new Response("Fixture type article not found", { status: 404 })

    await db.delete(fixtureTypeArticles).where(eq(fixtureTypeArticles.id, data.id))

    await logAuditEvent(
      {
        action: "fixtureTypeArticle.delete",
        entityType: "fixtureTypeArticle",
        entityId: data.id,
        entityName: `${existing.shelveType}/${existing.articleNumber}`,
        summary: `Removed article "${existing.articleNumber}" from fixture type "${existing.shelveType}"`,
      },
      request,
    )

    return { success: true }
  })
